import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { APP_APPROVALS, APP_CATEGORIES } from '@ward/core';
import { api, qs, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { Account, Ago, Approval, Button, catLabel, clickableRow, DecisionTag, Empty, EntityLink, Figure, Figures, fmtDate, fmtNum, ListToolbar, MutationError, Outcome, PageHead, PAGE_SIZE, Pager, Q, Section, Seg, SortTh, statusText, Tag, useFilters, type Filters } from '../ui/kit';
import { APPROVAL } from '../ui/format';
import { StackedDaily } from '../ui/charts';
import { actionLabel, EventTable, useEventDrawer } from './Activity';

type Window = '7' | '30' | '90';

/** "3 corp · 1 personal" (· N unrecognised): the accounts seen in an application, one column. */
function Accounts({ a }: { a: { corporate_accounts: number; personal_accounts: number; unknown_accounts: number } }) {
  const parts = [
    a.corporate_accounts ? <span key="c">{a.corporate_accounts} corp</span> : null,
    a.personal_accounts ? <span key="p" className="text-block">{a.personal_accounts} personal</span> : null,
    a.unknown_accounts ? <span key="u" className="muted">{a.unknown_accounts} unrecognised</span> : null,
  ].filter(Boolean);
  if (parts.length === 0) return <span className="muted">—</span>;
  return <span className="nowrap">{parts.flatMap((p, i) => (i ? [' · ', p] : [p]))}</span>;
}

export function Apps() {
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  const nav = useNavigate();
  const can = useCan();
  const ai = sp.get('view') === 'ai';
  const days = (sp.get('days') ?? '30') as Window;
  const q = useApi<{ total: number; items: any[] }>(`/apps${qs({
    q: sp.get('q'), category: sp.get('category'), approval: sp.get('approval'), discovered: sp.get('discovered'), days: sp.get('days'),
    ai: ai ? 'true' : undefined, sort: sp.get('sort'), limit: PAGE_SIZE, offset,
  })}`);
  const [adding, setAdding] = useState(false);
  return (
    <>
      <PageHead
        title={ai ? 'AI applications' : 'Applications'}
        sub={ai
          ? 'Generative AI tools in use, whether people use them with corporate or personal accounts, and what data they tried to share. Prompts are never collected — only that a submission happened.'
          : 'Every web application your enrolled browsers have used — catalogued or newly discovered — with who uses it, from which kind of account, and what data moved.'}
        actions={<>
          <Seg label="View" value={ai ? 'ai' : 'all'} onChange={(v) => filters.setSp(v === 'ai' ? { view: 'ai' } : {}, { replace: true })} options={[['all', 'All applications'], ['ai', 'AI applications']]} />
          {can('apps:write') && <Button onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : 'Add custom application'}</Button>}
        </>}
      />
      {adding && <NewApp onDone={() => setAdding(false)} />}
      {ai && <AiSummary days={days} />}
      <ListToolbar filters={filters} placeholder="Search name or hostname…" keep={['view']}>
        {!ai && (
          <select aria-label="Category" value={sp.get('category') ?? ''} onChange={(e) => set('category', e.target.value)}>
            <option value="">All categories</option>{APP_CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        )}
        <select aria-label="Approval" value={sp.get('approval') ?? ''} onChange={(e) => set('approval', e.target.value)}>
          <option value="">Any approval</option>{APP_APPROVALS.map((a) => <option key={a} value={a}>{statusText(APPROVAL, a)}</option>)}
        </select>
        <Seg label="Window" value={days} onChange={(v) => set('days', v === '30' ? '' : v)} options={[['7', '7d'], ['30', '30d'], ['90', '90d']]} />
        <label className="check"><input type="checkbox" checked={sp.get('discovered') === 'false'} onChange={(e) => set('discovered', e.target.checked ? 'false' : '')} /> Include never-seen catalog apps</label>
      </ListToolbar>
      <Q q={q}>
        {(d) =>
          d.items.length === 0 ? <Empty title="No applications match">Applications appear as enrolled browsers visit them.</Empty> : (
            <>
              <AppTable rows={d.items} filters={filters} onOpen={(id) => nav(`/discover/apps/${id}`)} />
              <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
            </>
          )
        }
      </Q>
    </>
  );
}

function AppTable({ rows, filters, onOpen }: { rows: any[]; filters: Filters; onOpen: (id: string) => void }) {
  return (
    <div className="table-wrap">
      <table className="t">
        <thead><tr><th>Application</th><th>Category</th><th>Approval</th><th className="num">Users</th><th>Accounts</th><th className="num">Sensitive actions</th><th>Policy</th><SortTh filters={filters} col="last_seen_at">Last seen</SortTh></tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} {...clickableRow(() => onOpen(a.id))}>
              <td><EntityLink to={`/discover/apps/${a.id}`}>{a.name}</EntityLink>{a.source === 'discovered' && <> <Tag tone="info" dot={false}>New</Tag></>}<div className="sub mono">{a.hosts[0]}</div></td>
              <td>{catLabel(a.category)}{a.suspected_ai && <div className="sub">suspected AI</div>}</td>
              <td><Approval a={a.approval} /></td>
              <td className="num">{a.users}</td>
              <td><Accounts a={a} /></td>
              <td className="num">{a.sensitive_actions || '—'}{a.blocked ? <div className="sub">{a.blocked} blocked</div> : null}</td>
              <td>{a.policy_covered ? <Tag tone="allow">Covered</Tag> : <Tag tone="neutral">None</Tag>}</td>
              <td className="sub"><Ago at={a.last_seen_at} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The AI view's headline numbers, daily chart and policy coverage (above the application table filtered to AI). */
function AiSummary({ days }: { days: Window }) {
  const q = useApi<any>(`/ai/overview?days=${days}`);
  return (
    <Q q={q} rows={3}>
      {(d) => {
        const acct = (t: string) => (d.byAccount as any[]).find((x) => x.account_type === t)?.users ?? 0;
        const sum = (pred: (r: any) => boolean, k = 'n') => (d.actions as any[]).filter(pred).reduce((s, r) => s + r[k], 0);
        return (
          <>
            <Figures label="AI usage">
              <Figure label="AI applications" value={d.apps.length} sub={`${(d.apps as any[]).filter((a) => a.approval === 'unreviewed').length} unreviewed`} />
              <Figure label="People using AI" value={fmtNum(d.totals?.users)} sub={`on ${fmtNum(d.totals?.devices)} devices`} />
              <Figure label="With personal accounts" value={acct('personal')} tone={acct('personal') ? 'block' : undefined} sub={`${acct('corporate')} with corporate · ${acct('unknown')} unrecognised`} />
              <Figure label="Prompt submissions" value={fmtNum(sum((r) => r.action === 'submit'))} sub="metadata only" />
              <Figure label="File uploads" value={fmtNum(sum((r) => r.action === 'upload'))} />
              <Figure label="Sensitive detections" value={fmtNum(sum(() => true, 'sensitive'))} sub={`${fmtNum(sum((r) => r.outcome === 'blocked'))} blocked`} href="/investigate/activity?category=genai&sensitiveOnly=true" />
            </Figures>
            <div className="cols cols-3-2">
              <Section title="AI activity by day" aside={<Link to="/investigate/activity?category=genai">View AI activity</Link>}>
                {d.daily.length === 0 ? <Empty title="No AI activity reported" /> : (
                  <StackedDaily data={(d.daily as any[]).map((x) => ({ ...x, other: Math.max(0, x.prompts + x.pastes + x.uploads - x.blocked - x.warned) }))}
                    series={[{ key: 'blocked', label: 'Blocked', color: 'var(--series-block)' }, { key: 'other', label: 'Allowed', color: 'var(--series-allow)' }, { key: 'warned', label: 'Warned / justified', color: 'var(--series-warn)' }]} />
                )}
              </Section>
              <Section title="Policy coverage" aside={<Link to="/protect/ai">Manage AI protection</Link>}>
                {d.policies.length === 0 ? <Empty title="No AI policies">Without a policy, AI tools are only discovered, not controlled.</Empty> : (
                  <table className="t"><tbody>{(d.policies as any[]).map((p) => <tr key={p.id}><td><Link to={`/manage/policies/${p.id}`}>{p.name}</Link>{!p.enabled && <span className="sub"> (disabled)</span>}</td><td><DecisionTag d={p.action} /></td></tr>)}</tbody></table>
                )}
              </Section>
            </div>
          </>
        );
      }}
    </Q>
  );
}

function NewApp({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: '', category: 'unknown', approval: 'unreviewed', hosts: '', accountSelector: '' });
  const create = useAction(
    () => api('/apps', { method: 'POST', body: { name: f.name, category: f.category, approval: f.approval, hosts: f.hosts.split(/[\s,]+/).filter(Boolean), accountSelector: f.accountSelector || null } }),
    onDone,
  );
  return (
    <Section title="Custom application" aside="for internal or niche apps the catalog does not know">
      <form className="form-grid mt-12" onSubmit={(e) => { e.preventDefault(); create.mutate(undefined); }}>
        <label className="field">Name<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="field">Hostnames <span className="hint">space-separated; *.example.com for subdomains</span><input required value={f.hosts} onChange={(e) => setF({ ...f, hosts: e.target.value })} placeholder="tool.example.com" /></label>
        <label className="field">Category<select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{APP_CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}</select></label>
        <label className="field">Approval<select value={f.approval} onChange={(e) => setF({ ...f, approval: e.target.value })}>{APP_APPROVALS.map((a) => <option key={a} value={a}>{statusText(APPROVAL, a)}</option>)}</select></label>
        <label className="field">Account element <span className="hint">optional CSS selector showing the signed-in email</span><input value={f.accountSelector} onChange={(e) => setF({ ...f, accountSelector: e.target.value })} placeholder="#account-email" /></label>
        <div className="self-end"><Button type="submit" variant="primary" disabled={create.isPending}>Add application</Button></div>
      </form>
      <MutationError error={create.error} />
    </Section>
  );
}

export function AppDetail() {
  const { id } = useParams();
  const can = useCan();
  const q = useApi<any>(`/apps/${id}`);
  const ev = useEventDrawer();
  const patch = useAction((b: object) => api(`/apps/${id}`, { method: 'PATCH', body: b }));
  return (
    <Q q={q}>
      {({ app: a, daily, users, accounts, movement, policies, recentEvents }) => (
        <>
          <PageHead
            crumbs={<Link to="/discover/apps">Applications</Link>}
            title={a.name}
            sub={<><span className="mono">{a.hosts.join(', ')}</span> · {a.source === 'discovered' ? 'discovered by browsers' : a.source === 'custom' ? 'custom application' : 'catalog application'}</>}
            actions={
              <>
                {can('apps:write') ? (
                  <>
                    <label className="field">Category
                      <select value={a.category} onChange={(e) => patch.mutate({ category: e.target.value })}>{APP_CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}</select>
                    </label>
                    <label className="field">Approval
                      <select value={a.approval} onChange={(e) => patch.mutate({ approval: e.target.value })}>{APP_APPROVALS.map((x) => <option key={x} value={x}>{statusText(APPROVAL, x)}</option>)}</select>
                    </label>
                  </>
                ) : (<><span>{catLabel(a.category)}</span><Approval a={a.approval} /></>)}
                <Link className="btn" to={`/investigate/activity?app=${a.id}`}>View activity</Link>
              </>
            }
          />
          <MutationError error={patch.error} />
          <dl className="facts">
            <dt>First seen</dt><dd>{fmtDate(a.first_seen_at)}</dd>
            <dt>Last seen</dt><dd>{fmtDate(a.last_seen_at)}</dd>
            <dt>Account detection</dt><dd>{a.identity_provider ?? 'generic'}{a.account_selector && <> · selector <code>{a.account_selector}</code></>}</dd>
            {a.notes && (<><dt>Notes</dt><dd>{a.notes}</dd></>)}
          </dl>
          <div className="cols cols-3-2">
            <Section title="Activity" aside="last 30 days">
              {daily.length === 0 ? <Empty title="No recorded activity" /> : (
                // "sensitive" includes blocked sensitive actions; plot the remainder so the stack does not double count.
                <StackedDaily
                  data={daily.map((d: any) => ({ ...d, sensitive_unblocked: Math.max(0, d.sensitive - d.blocked) }))}
                  series={[{ key: 'blocked', label: 'Blocked', color: 'var(--series-block)' }, { key: 'sensitive_unblocked', label: 'Sensitive, not blocked', color: 'var(--series-allow)' }]}
                />
              )}
            </Section>
            <Section title="Policies affecting this application">
              {policies.length === 0 ? <Empty title="No policy covers this application">It is only subject to catch-all rules, if any.</Empty> : (
                <table className="t"><tbody>{policies.map((p: any) => <tr key={p.id}><td><Link to={`/manage/policies/${p.id}`}>{p.name}</Link>{!p.enabled && <span className="sub"> (disabled)</span>}</td><td><DecisionTag d={p.action} /></td></tr>)}</tbody></table>
              )}
            </Section>
          </div>
          <div className="cols cols-2">
            <Section title="Accounts observed" aside="identifiers only for corporate accounts by default">
              {accounts.length === 0 ? <Empty title="No accounts detected yet" /> : (
                <table className="t"><thead><tr><th>Account</th><th>Identifier</th><th className="num">Users</th><th>Last seen</th></tr></thead>
                  <tbody>{accounts.map((x: any, i: number) => <tr key={i}><td><Account t={x.account_type} domain={x.account_domain} /></td><td className="sub">{x.identifier ?? '—'}</td><td className="num">{x.users}</td><td className="sub"><Ago at={x.last_seen_at} /></td></tr>)}</tbody></table>
              )}
            </Section>
            <Section title="Data movement" aside="last 30 days">
              {movement.length === 0 ? <Empty title="No data movement recorded" /> : (
                <table className="t"><thead><tr><th>Action</th><th>Account</th><th>Outcome</th><th className="num">Events</th></tr></thead>
                  <tbody>{movement.map((m: any, i: number) => <tr key={i}><td className="capitalize">{actionLabel(m.action)}</td><td><Account t={m.account_type} /></td><td><Outcome o={m.outcome} /></td><td className="num">{m.n}</td></tr>)}</tbody></table>
              )}
            </Section>
          </div>
          <Section title="Users" aside="last 30 days">
            {users.length === 0 ? <Empty title="No identified users" /> : (
              <table className="t"><thead><tr><th>User</th><th>Department</th><th>Accounts</th><th className="num">Visits</th><th className="num">Sensitive</th><th className="num">Blocked</th><th>Last day</th></tr></thead>
                <tbody>{users.map((u: any) => <tr key={u.id}><td><Link to={`/discover/users/${u.id}`}>{u.name}</Link></td><td className="sub">{u.department ?? '—'}</td><td><div className="tags">{u.account_types.map((t: string) => <Account key={t} t={t} />)}</div></td><td className="num">{u.visits}</td><td className="num">{u.sensitive}</td><td className="num">{u.blocked}</td><td className="sub">{u.last_day}</td></tr>)}</tbody></table>
            )}
          </Section>
          <Section title="Recent security events" aside={<Link to={`/investigate/activity?app=${a.id}`}>View all activity</Link>}><EventTable rows={recentEvents} onOpen={ev.open} /></Section>
          {ev.drawer}
        </>
      )}
    </Q>
  );
}
